import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import CssBaseline from '@mui/material/CssBaseline';

// Load layered utility styles before the unlayered MUI baseline.
import 'flyt/theme.css';
import './theme/flyt-overrides.css';

import AppProviders from 'web/contexts/AppProviders';
import AuthorizedAppProviders from 'web/contexts/AuthorizedAppProviders';
import { useUser } from 'web/contexts/UserContext';

import Loading from 'components/Loading';

const UnauthorizedApp = lazy(() => import('web/screens/UnauthorizedApp'));

const AdminRoutes = lazy(() => import('web/routes/AdminRoutes'));
const DepartmentAdminRoutes = lazy(
	() => import('web/routes/DepartmentAdminRoutes')
);
const AccountantRoutes = lazy(() => import('web/routes/AccountantRoutes'));
const AuditorRoutes = lazy(() => import('web/routes/AuditorRoutes'));
const UserRoutes = lazy(() => import('web/routes/UserRoutes'));
const SilentUserScreen = lazy(() => import('web/screens/SilentUserScreen'));

function App() {
	const { user, userHasRole, userLoading } = useUser();

	if (userLoading) {
		return <Loading />;
	}

	if (user && user.role) {
		return (
			<AuthorizedAppProviders>
				<Suspense fallback={<Loading />}>
					{userHasRole('admin') && <AdminRoutes />}
					{userHasRole('departmentAdmin') && (
						<DepartmentAdminRoutes />
					)}
					{userHasRole('accountant') && <AccountantRoutes />}
					{userHasRole('auditor') && <AuditorRoutes />}
					{userHasRole('user') && <UserRoutes />}
					{userHasRole('silent') && <SilentUserScreen />}
				</Suspense>
			</AuthorizedAppProviders>
		);
	}

	return (
		<Suspense fallback={<Loading />}>
			<UnauthorizedApp />
		</Suspense>
	);
}

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
	<AppProviders>
		<CssBaseline />
		<App />
	</AppProviders>
);
